import { ComponentProps, ReactElement } from 'react';
import { InvoiceTrip, InvoiceTripCombineTypeEnum, RunPreviewInvoiceResponse } from '@/models/gen/graphql';
import { formatNumber, getClasses } from '@/utils';

import InvoiceDetail from '@/features/PdfLayout/layouts/Invoice/InvoicePdfDetail';
import InvoicePdfDetailList from '@/features/PdfLayout/layouts/Invoice/InvoicePdfDetailList';
import { usePdfLayout } from '@/features/PdfLayout';

export type InvoiceMetricsProps = ComponentProps<'ul'>;

const LEGAL_COMBINE_TYPES = [InvoiceTripCombineTypeEnum.Legal, InvoiceTripCombineTypeEnum.LegalOverCapacity];

const InvoiceMetrics = ({ ...props }: InvoiceMetricsProps): ReactElement => {
  const { totalSavings, totalCombinedTrips, grandTotal, totalTrips, totalCharges, netBillableTrips } = usePdfLayout(
    ({ state }: { state: { data: RunPreviewInvoiceResponse } }) => ({
      totalSavings: state?.data?.output?.invoice?.hasDiscounts === 1 ? state?.data?.output?.invoice?.totalSavings : undefined,
      totalCombinedTrips:
        state?.data?.output?.invoice?.hasDiscounts === 1
          ? state?.data?.output?.trips?.filter(
              (trip: InvoiceTrip): boolean => trip?.combineId && LEGAL_COMBINE_TYPES.includes(trip.combineType)
            )?.length
          : undefined,
      grandTotal: state?.data?.output?.invoice?.grandTotal || 0,
      totalTrips: state?.data?.output?.invoice?.totalTripsBilled || state?.data?.output?.trips?.length || 0,
      totalCharges: state?.data?.output?.customCharges?.length || 0,
      netBillableTrips: state?.data?.output?.invoice?.netBillableTrips || 0,
    })
  );

  return (
    <InvoicePdfDetailList {...props} className={getClasses('InvoicePdf-Metrics', props.className)}>
      {totalTrips > 0 && (
        <InvoiceDetail bold name="Total Trips">
          <strong>{totalTrips}</strong>
        </InvoiceDetail>
      )}
      {totalCharges > 0 && (
        <InvoiceDetail bold name="Total Items">
          <strong>{totalCharges}</strong>
        </InvoiceDetail>
      )}
      {totalSavings > 0 && (
        <InvoiceDetail bold name="Combine Savings">
          <strong>USD {formatNumber.currency(totalSavings)}</strong>
        </InvoiceDetail>
      )}
      {netBillableTrips > 0 && (
        <InvoiceDetail bold name="Net Billable Trips">
          <strong>{netBillableTrips}</strong>
        </InvoiceDetail>
      )}
      {totalCombinedTrips > 0 && (
        <InvoiceDetail bold name="Total Combined Trips">
          <strong>{totalCombinedTrips}</strong>
        </InvoiceDetail>
      )}
      <InvoiceDetail bold name="Total Amount Due">
        <strong>USD {formatNumber.currency(grandTotal)}</strong>
      </InvoiceDetail>
    </InvoicePdfDetailList>
  );
};

export default InvoiceMetrics;
